import React from 'react';
import {observable, toJS, isObservable} from 'mobx'
import Loading from '../components/Loading'
import * as api from '../api'
import View from '../components/metadata-components/View'
import {createContext, setMissingComponentId} from '../utils/metadata-utils'
import qs, {ParsedQuery} from 'query-string'
import ObservableSpinner from '../components/ObservableSpinner'
import {handleApiResponse} from "../utils/events/actionEvent"
import IdleTimer from 'react-idle-timer'
import Chatbot from '../components/Chatbot'
import _ from 'lodash';
import {mergeDiffDeep} from "../utils/mobx-utils";
import {googleAnalyticsSetFlowAndView,googleAnalyticsInit} from '../utils/google-analytics-utils';
import {clearTracingFields, initTracingFields} from "../utils/tracing-util";
import {clearAllErrors} from '../utils/error-utils'
import ModalPopup from "../components/metadata-components/ModalPopup";
import Error404 from './Error404';
import Error5xx from './Error5xx';
import Timeout from './Timeout';
import {tlaToClientCode} from '../utils/tla-utils';
import { FormBean, ICloneContext, IViewContext, IContextObject, IFormBean, IUIState, UIRendererProps, UIRendererState, UIState } from '../datatypes';
import {v4 as uuidv4} from 'uuid';

// put const at the end to avoid compilation error: Import in body of module; reorder to top import/first
const {MDBAlert} = require('mdbreact')

declare global {
    interface Window { 
        flowName?: string; 
    }
}

function getSingleParm(queryParams:ParsedQuery, name:string):string | null {
    let v = queryParams[name];
    if(!v){
        return null;
    }
    if(_.isArray(v)){
        return (v as string[])[0];
    }
    return v as string;
}

export default class UIRenderer extends React.Component<UIRendererProps, UIRendererState> {
    private formBean: FormBean | undefined;
    private viewCtxMap: IViewContext;
    private clone_ctx: ICloneContext|undefined;

  constructor(props:UIRendererProps){
    super(props)
    this.state = {
        uiMetadata: null,
        popupViewMetadata:null,
        customCss: '',
        flowName: '',
        PopupViewName: '',
        tla: '',
        domainDataId: '',
        channelCode: '',
        hashedId:'',
    };

    this.viewCtxMap = {
      triggers: {},
    }
    console.debug(`UIRenderer ctor url=${window.location.href} props=${JSON.stringify(props,null,4)} `);
    let flowName = props.flowName || window.flowName || this.props.match.params.flowName || '';
    let queryParams = this.props.location ? qs.parse(this.props.location.search) : {};
    let tla = getSingleParm(queryParams, 'client');
    let domainDataId = props.domainDataId || getSingleParm(queryParams, 'id');
    let hashedId = getSingleParm(queryParams, 'hashedId');
    let channelCode = getSingleParm(queryParams, 'channelCode');

    this.state = {
      uiMetadata: null,
      popupViewMetadata:null,
      customCss: '',
      PopupViewName:'',
      flowName,
      tla,
      domainDataId,
      channelCode,
      hashedId,
      queryParams
    }
  }

  async loadMetadata() {
    let {flowName, tla, domainDataId, channelCode, hashedId, queryParams} = this.state;
    if(!tla) {
      this.setState({errorComponent: <Error404 message='no client specified'/>});
      return;
    }
    let clientCode = tlaToClientCode(tla)!;
    this.setState({
      isFetching: true
    });

    console.debug(`UIRender getInitUIState`)
    let uiState = await api.getInitUIState(flowName, clientCode, domainDataId, hashedId, channelCode, queryParams);

    if(!uiState) {
      this.setState({isFetching: false, errorComponent: <Error5xx message='failed to initialize state'/>});
      return;
    }

    this.initilizeGoogleAnalyticsFromState(uiState);
    this.initializeFormBeanFromState(uiState);
  }

  /**
   * Initilize Google Analytics from UI State
   * UI State will contain a Google Site Id tracker
   *
   * @param {Object} uiState
   * @param {string} uiState.googleSiteId
   */
  initilizeGoogleAnalyticsFromState = (uiState:IUIState) => {
    const {googleSiteId} = uiState;

    if(googleSiteId) {
      googleAnalyticsInit(googleSiteId);
      console.log(`Google Analytics is enabled. Google Site Id: ${googleSiteId}`)
    }
    else {
      console.log("Google Analytics not enabled. Google Site Id not provided in UI State")
    }
  }

  initializeFormBeanFromState = (uiState:IUIState) => {
    (uiState.viewMetadataList||[]).forEach(metadata=>{
        setMissingComponentId(metadata, undefined, undefined);
    })
    /*
      __ui__only: Space for particular UI components to store their state
      Currently enablePopupView and venmoState will be placed in __ui__only.
      Example usecase: PaymentMethod component gets unmounted during venmo tokenizing. The component can store its state before unmounting
      and can access it later after mounting.
     */
    const formBean:IFormBean = {
        __ui__only : {},
        getRendererState : () => this.state,
        getRendererProps : () => this.props,
        contextualErrors : {},
        errorCache : {},
        actionMessages : uiState.actionMessages,
        clientConfigOptions : uiState.clientConfigOptions,
        SubSystem : uiState.subSystem,
        ClientCode : uiState.clientCode,
        FlowName : uiState.flowName,
        ChannelCode : uiState.channelCode,
        CurrentViewName : uiState.currentViewName,
        ParentViewName : uiState.parentViewName, // TODO: need further implementation
        FlowStack : uiState.flowStack,
        CurrentObjectId : uiState.currentObjectId,
        CurrentObjectName : uiState.currentObjectName,
        DomainObjectName : uiState.domainObjectName,
        PluginData: uiState.pluginData,
        Biller : uiState.billerInfo,
        DomainMetadata : uiState.domainObjectMetadata,
        validators : [],
        ViewMetadataList : uiState.viewMetadataList, // TODO: double check if this is necessary
        FlowMetadata : uiState.flowMetadata,
        DomainObject : {}, // TODO: whether will have multiple Domain Objects in the same page?
        InstructionList : uiState.instructionList,
        NavigationInstructionList : uiState.navigationInstruction,
        JwtToken : uiState.jwtToken,
        valueListStore : uiState.valueListStore,  
        error5xx: ()=>{}, 
        eventMap: {},
        changeView: (...args) => {},
        switchFlow: async (...args)=>{},
        previousFlow: async (...args)=>{},
    }
    formBean.DomainObject[formBean.DomainObjectName] = uiState.domainObject;

    this.formBean = observable(formBean);
    let flatDomainDataCtx = createContext(this.formBean.DomainMetadata, this.formBean.DomainObject[this.formBean.DomainObjectName]);
    console.debug(`createContext`, flatDomainDataCtx);
    /*
    @param: name - Name of the view to which we need to switch.
    @param: ctxObj - Components can send their own ctxObjects that can be used while switching to a different view.
    @param: callback - callback that can be used by components
    @param: cancel - this is to indicated if the childView sent back cancel. In this scenario parent can ignore all changes from childView's ctx.
     */
    this.formBean.changeView = (name:string, 
                                ctxObj?:IContextObject, 
                                callback?: (flag:boolean)=>void, 
                                cancel?:boolean) => {
      let viewMetadata = this.formBean!.ViewMetadataList.find(view => view.name == name);
      if (!viewMetadata) {
        console.log("Unable to change view. View name: " + name + " does not exists.");
        this.setState({errorComponent: <Error5xx message={`View ${name} does not exist`} />})
        return;
      }
      console.debug(`change to view ${name}`);
      /*
      Stores a static clone of each context, before switching to the childView.
      When cancel is called, replaces the original ctx with the clonedCtx (after converting it to observable)
      This makes sure, any changes done inside the childView are cancellable when cancel is called.
       */
      if(cancel){
        let source = this["clone_ctx"]![name]!;
        /*
        Replacing the actual domain object values with the cloned ones. This makes sure the changes in childView are ignored
        backup option 2: Creating a new context using the cloned domainObject instead of calculating the diff.
         */
        // TODO: do we need complete RESET function ???
        if(this["clone_ctx"]) mergeDiffDeep(this.formBean!.DomainObject[this.formBean!.DomainObjectName], source);
      }else{
          // todo: what if there are multiple level of child views ???
        //If a view wants to send a ctx, we replace the name with the received view's Ctx. Eg: parentView can send childViewCtx inside the ctxObj.
        if(ctxObj) {
          if(ctxObj.operation && ctxObj.operation == "Add"){
            //Cloning before adding the ctx
            if(!this["clone_ctx"]){ 
                this["clone_ctx"] = { };
            }
            this["clone_ctx"]![this.formBean!.CurrentViewName] = JSON.parse(JSON.stringify(toJS(this.formBean!.DomainObject[this.formBean!.DomainObjectName])));
            this.viewCtxMap[name] = ctxObj.newCtx;

            //Sending callback that cloning has been completed, and the component can continue the ADD process.
            if(callback) callback(true);

          }else if(ctxObj.operation && ctxObj.operation === "Edit"){
            this.viewCtxMap[name] = ctxObj.newCtx;

            if(!this["clone_ctx"]){
                this["clone_ctx"] = {};
            }
            // this["clone_ctx"][this.formBean.CurrentViewName] = JSON.parse(JSON.stringify(toJS(this.ctx)));
            this["clone_ctx"]![this.formBean!.CurrentViewName] = JSON.parse(JSON.stringify(toJS(this.formBean!.DomainObject[this.formBean!.DomainObjectName])));
          }

          this.formBean!.CurrentObjectId = ctxObj.id;
          this.formBean!.CurrentObjectName = viewMetadata.domainObjectName;
        }
      }


      this.formBean!.validators = [];
      // todo: useles code?
    //   if(viewMetadata.eventHandlers){
    //     let eventMap = {};
    //     viewMetadata.eventHandlers.forEach(handler =>{
    //       eventMap[handler.event] = generateEventFunctions(handler.onEvent, handler.event, {formBean: this.formBean});
    //     });
    //     this.formBean.eventMap = eventMap;
    //   } else {
    //     this.formBean.eventMap = {};
    //   }
      this.formBean!.eventMap = {};
      if(uiState.flowMetadata && uiState.flowMetadata.description){
        document.title = uiState.flowMetadata.description;    
      }else{
        document.title = "Paymentus App";  
      }

      if(viewMetadata.popup) {
        //change to view has popup=true
        this.formBean!.__ui__only.enablePopUpView = true;
        this.formBean!.validators = [];

        this.setState({isFetching: false, popupViewMetadata: viewMetadata, PopupViewName:name});
      }else{
        this.formBean!.__ui__only.enablePopUpView = false;
        this.formBean!.CurrentViewName = name;

        this.setState({uiMetadata:viewMetadata});
        googleAnalyticsSetFlowAndView(this.formBean!.FlowName,this.formBean!.CurrentViewName);
      }

      // clear all errors after changing view
      clearAllErrors(this.formBean);
    };

    this.formBean.error5xx = () => {
      this.setState({errorComponent: <Error5xx message='HTTP 500 error' />});
    }

    //this funciton is async because during the switch flow, we want the button to be disabled or wait till its on click is done
    this.formBean.switchFlow = async (flowName:string, action:string) => {
      this.setState({
        isFetching: true
      });

      let switchFlowResponse = await api.switchFlow(this.formBean, flowName, action, {});
      if(!switchFlowResponse || !switchFlowResponse.result) {
        console.log("Unable to switch flow, no result");
        this.setState({errorComponent: <Error5xx message='Unable to switch flow, no result'/>})
        return;
      }
      let newState = switchFlowResponse.result;
      if(switchFlowResponse.error){
        console.log("Error occured while switching flow")
        if(this.formBean!.DomainObjectName != newState.domainObjectName){
          console.log("Domain objects have changed and there was an error, there is no way to handle this gracefully right now");
          this.setState({errorComponent: <Error5xx message='Domain objects have changed and there was an error.'/>})
          return;
        }
        //an error happened so we will stay on the same page, merge errors etc into current form bean
        await handleApiResponse(this.formBean!, switchFlowResponse)
        this.setState({
          isFetching: false
        });
        //this if statement is written in a way where it assumes nothing will happen after, this is to ensure that
        return;
      }
      //now that we have the new state, we can switch to initializing the new formbean
      //is fetching wasn't done earlier as this stops rendering the current flow overall, but now that we know we can
      //switch for sure, we can do that

      this.initializeFormBeanFromState(newState);

    };

    this.formBean.previousFlow = async (action: string|null) => {
      this.setState({
        isFetching: true
      });

      if(!action) {
        //this is a normal switch flow without transformation so we can load previous flow all together

        // this.setState({
        //   flowName: flowName,
        //   domainDataId: params.Id || this.state.domainDataId
        // }, () => {
        //   //this will automatically load new flow
        //   this.loadMetadata();
        // });
        return;
      }
      let switchFlowResponse = await api.previousFlow(this.formBean, action);
      if(!switchFlowResponse || !switchFlowResponse.result) {
        console.log("Unable to switch to previous flow, no result")
        this.setState({errorComponent: <Error5xx message='Unable to switch to previous flow'/>});
        return;
      }
      let newState = switchFlowResponse.result;

      if(switchFlowResponse.error){
        console.log("Error occured while switching to previous flow")
        if(this.formBean!.DomainObjectName != newState.domainObjectName){
          console.log("Domain objects have changed and there was an error, there is no way to handle this gracefully right now");
          this.setState({errorComponent: <Error5xx message='Domain objects have changed and there was an error.'/>});
          return;
        }
        //an error happened so we will stay on the same page, merge errors etc into current form bean
        await handleApiResponse(this.formBean!, switchFlowResponse)
        this.setState({
          isFetching: false
        });
        //this if statement is written in a way where it assumes nothing will happen after, this is to ensure that
        return;
      }
      //now that we have the new state, we can switch to initializing the new formbean
      //is fetching wasn't done earlier as this stops rendering the current flow overall, but now that we know we can
      //switch for sure, we can do that

      this.initializeFormBeanFromState(newState);

    };

    /*
     We change the format of this.ctx. ctx will now contain viewName:view's Ctx.
     This change is done, because while shifting from parent to child views and back, this makes it easier to track ctx
     and each ctx of a view is independent to the view.
    */
    for(let view of this.formBean.ViewMetadataList){
      this.viewCtxMap[view.name] = flatDomainDataCtx
    }

    this.setState({
      isFetching: false,
      customCss: uiState.globalCss
    });

    this.formBean.changeView(uiState.currentViewName);
  }

  componentDidMount() {
    initTracingFields();
    this.loadMetadata();
  }

  componentDidUpdate() {
}

  componentWillUnmount() {
    clearTracingFields();
  }

  render() {
    const callId = uuidv4();

    if(this.state.errorComponent){
      return this.state.errorComponent; // e.g Error404, Error5xx, Timeout
    }

    let {uiMetadata, isFetching, customCss, error, popupViewMetadata} =  this.state;
    let isLoading = isFetching || !uiMetadata;

    if(error){
        return <div className="d-flex align-items-center justify-content-center" style={{height: '100vh'}}>
                <MDBAlert color="danger">{error}</MDBAlert>
               </div>;
    }
    if(isLoading){
        return <Loading/>;
    }

    console.debug(`UIRender render ${callId} START. flow:${this.state.flowName} uiMetadata:${uiMetadata?.name}`, uiMetadata);
    const result = (
        <React.Fragment>
            <IdleTimer
                element={document}
                onIdle={() => {
                // it is problematic to use this.props.history.push to in background.
                // see https://jira.paymentus.io/browse/NEXG-2106 for details.
                this.setState({errorComponent: <Timeout/>})
                }}
                debounce={250}
                timeout={1000 * 60 * 15}//15 min timeout
            />
            <style type="text/css">{customCss}</style>

            {this.formBean!.FlowMetadata.enableChatbot ? 
             <Chatbot jwtToken={this.formBean!.JwtToken} tla={this.state.tla!}></Chatbot> : 
             null}
            {this.formBean!.__ui__only.enablePopUpView && 
             <ModalPopup isModal={true} isLoading={isLoading} metadata={popupViewMetadata!} 
                         formBean={this.formBean!} ctx={this.viewCtxMap[this.state.PopupViewName]}/>
            }
            <ObservableSpinner formBean={this.formBean!}/>
            <View metadata={uiMetadata!} formBean={this.formBean!} ctx={this.viewCtxMap[this.formBean!.CurrentViewName]}/>
        </React.Fragment>);
    console.debug(`UIRender render ${callId} END. flow:${this.state.flowName} uiMetadata:${uiMetadata?.name}`, uiMetadata);
    return result;
  }
}
